import { useState, useCallback, useMemo, useRef } from 'react'
import { Column } from './types'
import { getColumnTypes } from './helpers'

interface UseColumnsProps {
  initialColumns: Column[]
}

export default function useColumns({ initialColumns }: UseColumnsProps) {
  const [columns, setColumns] = useState<Column[]>(initialColumns)
  const nextIdRef = useRef(1)

  const generateColumnId = useCallback(() => {
    const id = `new_col_${nextIdRef.current}`
    nextIdRef.current += 1
    return id
  }, [])

  const generateOptionId = useCallback(() => {
    const id = `new_opt_${nextIdRef.current}`
    nextIdRef.current += 1
    return id
  }, [])

  const visibleColumns = useMemo(
    () => columns.filter((col) => !col._destroy),
    [columns]
  )

  const moveColumn = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const visible = columns.filter((col) => !col._destroy)
      const draggedColumn = visible[dragIndex]

      const newVisible = [...visible]
      newVisible.splice(dragIndex, 1)
      newVisible.splice(hoverIndex, 0, draggedColumn)

      const reordered = newVisible.map((col, idx) => ({
        ...col,
        position: idx + 1,
      }))

      const destroyed = columns.filter((col) => col._destroy)
      setColumns([...reordered, ...destroyed])
    },
    [columns]
  )

  const updateColumn = useCallback(
    (id: number | string, updates: Partial<Column>) => {
      setColumns((prev) =>
        prev.map((col) => (col.id === id ? { ...col, ...updates } : col))
      )
    },
    []
  )

  const removeColumn = useCallback((id: number | string) => {
    setColumns((prev) => {
      const column = prev.find((col) => col.id === id)
      if (!column) return prev

      if (typeof column.id === 'number') {
        return prev.map((col) =>
          col.id === id ? { ...col, _destroy: true } : col
        )
      } else {
        return prev.filter((col) => col.id !== id)
      }
    })
  }, [])

  const addColumn = useCallback(() => {
    const maxPosition = Math.max(0, ...visibleColumns.map((col) => col.position))
    const columnTypes = getColumnTypes()
    const newColumn: Column = {
      id: generateColumnId(),
      type: columnTypes[0].value,
      key: '',
      label: '',
      position: maxPosition + 1,
      required: false,
      system_column: true,
      settings: {},
    }
    setColumns((prev) => [...prev, newColumn])
  }, [visibleColumns, generateColumnId])

  const duplicateColumn = useCallback(
    (columnId: number | string) => {
      const columnToDuplicate = columns.find((col) => col.id === columnId)
      if (!columnToDuplicate || columnToDuplicate.system_column) return

      const maxPosition = Math.max(0, ...visibleColumns.map((col) => col.position))

      const duplicatedColumn: Column = {
        id: generateColumnId(),
        type: columnToDuplicate.type,
        key: `${columnToDuplicate.key}_copy`,
        label: `${columnToDuplicate.label} (copy)`,
        position: maxPosition + 1,
        required: columnToDuplicate.required,
        system_column: false,
        settings: { ...columnToDuplicate.settings },
        column_options: columnToDuplicate.column_options
          ?.filter((opt) => !opt._destroy)
          .map((opt) => ({
            id: generateOptionId(),
            label: opt.label,
            value: opt.value,
            color: opt.color,
            position: opt.position,
          })),
      }

      setColumns((prev) => [...prev, duplicatedColumn])
    },
    [columns, visibleColumns, generateColumnId, generateOptionId]
  )

  return {
    columns,
    visibleColumns,
    generateOptionId,
    moveColumn,
    updateColumn,
    removeColumn,
    addColumn,
    duplicateColumn,
  }
}
